import { useState } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { MagnifyingGlass, Export, Eye } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  en_attente: 'En attente',
  validee: 'Validée',
  en_route: 'En route',
  en_cours: 'En cours',
  terminee: 'Terminée',
  encaissee: 'Encaissée',
  annulee: 'Annulée',
};
const STATUS_COLOR = {
  en_attente: 'bg-amber-100 text-amber-700',
  validee: 'bg-blue-100 text-blue-700',
  en_route: 'bg-indigo-100 text-indigo-700',
  en_cours: 'bg-violet-100 text-violet-700',
  terminee: 'bg-emerald-100 text-emerald-700',
  encaissee: 'bg-green-100 text-green-700',
  annulee: 'bg-red-100 text-red-700',
};

export default function InterventionListView({ interventions, onSelect, scope, title }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exactAmount, setExactAmount] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');

  const filtered = interventions.filter((i) => {
    if (search) {
      const q = search.toLowerCase();
      const matches =
        (i.client_name || '').toLowerCase().includes(q) ||
        (i.address || '').toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (dateFrom || dateTo) {
      const ref = i.announced_time || i.created_at;
      if (!ref) return false;
      const d = new Date(ref);
      if (dateFrom && d < new Date(dateFrom + 'T00:00:00')) return false;
      if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
    }
    if (exactAmount) {
      const t = parseFloat(exactAmount);
      if (!Number.isNaN(t) && Math.abs((i.price || 0) - t) >= 0.01) return false;
    }
    return true;
  });

  const resetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setExactAmount('');
  };

  const doExport = async (format) => {
    try {
      const qs = new URLSearchParams({ scope, format });
      if (exportFrom) qs.set('date_from', exportFrom);
      if (exportTo) qs.set('date_to', exportTo);
      const res = await axios.get(`${API}/api/export/interventions?${qs}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const ext = format === 'xlsx' ? 'xlsx' : 'pdf';
      const mime =
        format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${scope}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      setShowExport(false);
    } catch {
      toast.error('Erreur export');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {isAdmin && (
          <Button
            variant="outline"
            onClick={() => setShowExport(true)}
            className="gap-2 border-reco-500 text-reco-600"
            data-testid={`${scope}-export-btn`}
          >
            <Export size={16} />
            Exporter
          </Button>
        )}
      </div>

      {/* Filter bar — single horizontal row */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par client ou adresse..."
            className="pl-8 h-9 bg-white"
            data-testid={`${scope}-search-input`}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Du</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[140px] bg-white" data-testid={`${scope}-date-from`} />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Au</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[140px] bg-white" data-testid={`${scope}-date-to`} />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-gray-500 whitespace-nowrap">Montant</Label>
          <Input type="number" step="0.01" value={exactAmount} onChange={(e) => setExactAmount(e.target.value)} placeholder="Ex: 300" className="h-9 w-[90px] bg-white" data-testid={`${scope}-amount-filter`} />
        </div>
        {(search || dateFrom || dateTo || exactAmount) && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-reco-600 hover:text-reco-700 underline ml-auto whitespace-nowrap"
            data-testid={`${scope}-reset-filters`}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          Aucune intervention trouvée
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {filtered.map((i, idx) => {
            const ref = i.announced_time || i.created_at;
            const d = ref ? new Date(ref) : null;
            const dateStr = d ? d.toLocaleDateString('fr-FR') : '—';
            const timeStr = d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
            const statusLabel = STATUS_LABEL[i.status] || i.status;
            const statusColor = STATUS_COLOR[i.status] || 'bg-gray-100 text-gray-700';
            return (
              <div
                key={i.id}
                className={`px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-reco-50/30 transition-colors`}
                data-testid={`${scope}-row-${i.id}`}
              >
                <div className="w-[120px] flex-shrink-0">
                  <div className="text-sm font-medium text-gray-900">{dateStr}</div>
                  <div className="text-xs text-gray-500">{timeStr}</div>
                </div>
                <div className="w-[170px] flex-shrink-0 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{i.client_name || '—'}</div>
                  <div className="text-xs text-gray-500">{i.intervention_type || ''}</div>
                </div>
                <div className="flex-1 min-w-0 text-sm text-gray-600 truncate">{i.address || '—'}</div>
                <div className="w-[90px] flex-shrink-0 text-sm font-semibold text-reco-600 text-right">
                  {typeof i.price === 'number' ? `${i.price.toFixed(2)} €` : '—'}
                </div>
                <div className="w-[110px] flex-shrink-0">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                    {statusLabel}
                  </span>
                </div>
                <div className="w-[110px] flex-shrink-0 text-xs text-gray-500 truncate">
                  {i.assigned_technician_name || '—'}
                </div>
                <Button variant="outline" size="sm" onClick={() => onSelect(i)} className="flex-shrink-0" title="Voir détail" data-testid={`${scope}-detail-${i.id}`}>
                  <Eye size={14} />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exporter {title.toLowerCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Du</Label>
                <Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} data-testid={`${scope}-export-from`} />
              </div>
              <div>
                <Label>Au</Label>
                <Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} data-testid={`${scope}-export-to`} />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => doExport('xlsx')} className="flex-1" data-testid={`${scope}-export-xlsx`}>Excel (.xlsx)</Button>
              <Button onClick={() => doExport('pdf')} variant="outline" className="flex-1" data-testid={`${scope}-export-pdf`}>PDF</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
